/**
 * HTTP Client for Backend APIs
 * 
 * Provides both authenticated (with JWT tokens) and non-authenticated HTTP clients.
 * Admin endpoints require authentication, regular endpoints use email-based auth.
 */

import { httpRequest, HttpOptions, ApiResult } from './httpClient';
import { apiRequest } from '../config/authConfig';
import { AuthenticationResult, IPublicClientApplication } from '@azure/msal-browser';

// Global MSAL instance reference (set by AuthContext)
let msalInstance: IPublicClientApplication | null = null;

/**
 * Set MSAL instance for token acquisition
 * Called by AuthContext during initialization
 */
export function setMsalInstance(instance: IPublicClientApplication): void {
    msalInstance = instance;
}

/**
 * Get the current MSAL instance (for direct token acquisition in services)
 */
export function getMsalInstance(): IPublicClientApplication | null {
    return msalInstance;
}

/**
 * Get access token for backend API calls
 */
async function getAccessToken(): Promise<string | null> {
    if (!msalInstance) {
        console.warn('MSAL instance not available for token acquisition');
        return null;
    }

    // Check if API scopes are configured
    if (!apiRequest.scopes || apiRequest.scopes.length === 0) {
        console.warn('No API scopes configured for backend authentication');
        return null;
    }

    try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) {
            return null;
        }

        const request = {
            ...apiRequest,
            account: accounts[0],
        };

        // Try silent token acquisition first
        const response = await msalInstance.acquireTokenSilent(request);
        return response.accessToken;
    } catch (error) {
        console.warn('Failed to acquire access token silently:', error);

        try {
            // Fallback to popup if silent acquisition fails
            const response = await msalInstance.acquireTokenPopup(apiRequest);
            return response.accessToken;
        } catch (popupError) {
            console.error('Failed to acquire access token via popup:', popupError);
            return null;
        }
    }
}

/**
 * HTTP request with optional authentication
 */
async function authRequest<T>(
    path: string,
    options: HttpOptions = {},
    requireAuth = false
): Promise<ApiResult<T>> {
    const headers: Record<string, string> = {};

    if (requireAuth) {
        const token = await getAccessToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        } else if (apiRequest.scopes.length > 0) {
            // Token required but not available
            throw new Error('Authentication required but access token not available');
        }
    }

    return httpRequest<T>(path, {
        ...options,
        headers: { ...headers, ...options.headers }
    });
}

/**
 * Simple HTTP request without authentication headers
 */
async function simpleRequest<T>(
    path: string,
    options: HttpOptions = {}
): Promise<ApiResult<T>> {
    return authRequest<T>(path, options, false);
}

/**
 * HTTP Client for email-based APIs (no authentication needed)
 */
export const httpClient = {
    // GET request
    get: <T>(path: string, options?: HttpOptions) =>
        simpleRequest<T>(path, { ...options, method: 'GET' }),

    // POST request
    post: <T>(path: string, data?: any, options?: HttpOptions) =>
        simpleRequest<T>(path, {
            ...options,
            method: 'POST',
            body: data instanceof FormData ? data : JSON.stringify(data)
        }),

    // PUT request
    put: <T>(path: string, data?: any, options?: HttpOptions) =>
        simpleRequest<T>(path, {
            ...options,
            method: 'PUT',
            body: data instanceof FormData ? data : JSON.stringify(data)
        }),

    // DELETE request
    delete: <T>(path: string, options?: HttpOptions) =>
        simpleRequest<T>(path, { ...options, method: 'DELETE' }),

    // PATCH request
    patch: <T>(path: string, data?: any, options?: HttpOptions) =>
        simpleRequest<T>(path, {
            ...options,
            method: 'PATCH',
            body: data instanceof FormData ? data : JSON.stringify(data)
        })
};

/**
 * HTTP Client for authenticated admin APIs (requires JWT tokens)
 */
export const authenticatedHttpClient = {
    // GET request with authentication
    get: <T>(path: string, options?: HttpOptions) =>
        authRequest<T>(path, { ...options, method: 'GET' }, true),

    // POST request with authentication
    post: <T>(path: string, data?: any, options?: HttpOptions) =>
        authRequest<T>(path, {
            ...options,
            method: 'POST',
            body: data instanceof FormData ? data : JSON.stringify(data)
        }, true),

    // PUT request with authentication
    put: <T>(path: string, data?: any, options?: HttpOptions) =>
        authRequest<T>(path, {
            ...options,
            method: 'PUT',
            body: data instanceof FormData ? data : JSON.stringify(data)
        }, true),

    // DELETE request with authentication
    delete: <T>(path: string, options?: HttpOptions) =>
        authRequest<T>(path, { ...options, method: 'DELETE' }, true),

    // PATCH request with authentication
    patch: <T>(path: string, data?: any, options?: HttpOptions) =>
        authRequest<T>(path, {
            ...options,
            method: 'PATCH',
            body: data instanceof FormData ? data : JSON.stringify(data)
        }, true)
};

// Export the existing unwrapApiResult function for backward compatibility
export { unwrapApiResult } from './httpClient';
export type { ApiResult, ApiError, ApiSuccess, ApiFailure } from './httpClient';